import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import type { PartProfile } from './partProfile';
import { useMemo } from 'react';
import { getInsertion, useInsertion, type Insertion } from './insertionRun';

// HOW BIG THE 3D MODEL IS, per part, in millimetres — and why it is not `PartItem.size`.
//
// They are two different facts about one part and they are allowed to disagree.
//
// `PartItem.size` is what the MANUAL says: read off its dimension drawings, or estimated from
// a photograph against a part that was measured. It is the part as specified — the number to
// hold a reconstruction up against, which is exactly what the viewer's "wanted" line is for.
//
// This is what the MESH ACTUALLY MEASURES. A reconstruction of a rail comes out 81 mm tall
// where the drawing says 82; that gap is a reading, and writing either number over the other
// destroys it. Which is what happened for a day: the mesh's figures were merged into the size
// record to fill the axes the drawing never printed, and the part then had one number wearing
// two meanings — the drawing's depth relabelled an estimate, and the wanted line comparing the
// mesh against itself.
//
// AND THIS ONE IS THE AUTHORITY DOWNSTREAM. Everything that puts a point ON a part — a hole's
// face and its two distances, a contact where two parts meet — is about the object that will
// be drawn and drilled, and that object is the mesh. A face is a rectangle cut out of the
// mesh's box; a hole 40 mm from the left end of a 393 mm rail is not 40 mm from the left end
// of a 398 mm one.
//
// IN MILLIMETRES AND NOT FILE UNITS, because a reconstruction is unitless — about one unit
// across — and its millimetres come from mmScale anchoring on the first axis the drawing DID
// give. So this is derived from both and is neither, which is the last reason it needs a
// field of its own.
export type ModelSize = {
  x: number;
  y: number;
  z: number;
  // WHICH MESH THESE NUMBERS CAME OFF: the part's model in the Parts DB (`job`), at its version
  // (`at`, meshStamp — the server moves it on every write). THE MODEL IS THE AUTHORITY, and this
  // is measured off it whenever a manual is open (see ModelSizeSync) — kept, drilled, rescaled,
  // the version moves and the record is measured again. Nothing else writes it: the viewer used
  // to, off whatever mesh was open in its modal, and that record outranked the model's.
  job?: number;
  at?: number;
  // HOW IT GOES INTO ITS HOLE, as the insertion table answered it (see insertionRun) — not
  // measured, and not stored here: joined on read (useModelSizes, getModelSizes), so everything
  // handed the part's sizes is handed this with them. Hardware only, and only where the part has
  // a model.
  insertion?: Insertion;
};

// part index -> what its mesh measures.
export type ByPart = Record<string, ModelSize>;

const key = (docId: string) => `notice.parts.modelSize.${docId}`;

const store = createDocEntryStore<{ byPart: ByPart }>((docId) => ({
  snapshot: { byPart: readState<ByPart>(docId, key(docId), {}) },
}));

// THE MEASURED SIZES WITH THE INSERTION TABLE'S ANSWER JOINED ON, where the part has both.
const joined = (byPart: ByPart, insertion: Record<string, Insertion>): ByPart => {
  const ids = Object.keys(insertion).filter((id) => byPart[id]);
  if (!ids.length) return byPart;
  const out = { ...byPart };
  ids.forEach((id) => { out[id] = { ...out[id], insertion: insertion[id] }; });
  return out;
};

export function useModelSizes(docId: string): { byPart: ByPart } {
  const { byPart } = store.useSnapshot(docId);
  const insertion = useInsertion(docId).byPart;
  return useMemo(() => ({ byPart: joined(byPart, insertion) }), [byPart, insertion]);
}

export const getModelSizes = (docId: string): ByPart =>
  joined(store.get(docId).byPart, getInsertion(docId).byPart);

// WRITTEN WHEN THE MODEL IS MEASURED, and only when the answer actually moved: every write is a
// doc-state save on a document several people have open, and a tenth of a millimetre is under
// what any of this is read to.
export function setModelSize(docId: string, index: number, size: ModelSize): void {
  const all = store.get(docId).byPart;
  const had = all[String(index)];
  const same = had
    && had.job === size.job
    && had.at === size.at
    && Math.abs(had.x - size.x) < 0.1
    && Math.abs(had.y - size.y) < 0.1
    && Math.abs(had.z - size.z) < 0.1;
  if (same) return;
  // Rounded to two decimals, which is what the numbers are worth: the mesh is measured off a
  // triangle soup and anchored by one figure off a drawing.
  const round = (v: number) => Math.round(v * 100) / 100;
  const next: ByPart = {
    ...all,
    [String(index)]: {
      x: round(size.x),
      y: round(size.y),
      z: round(size.z),
      ...(size.job == null ? {} : { job: size.job }),
      ...(size.at == null ? {} : { at: size.at }),
    },
  };
  store.patch(docId, { byPart: next });
  writeState(docId, key(docId), next);
}

// AND NONE, for a part with no model in the Parts DB: a record left from a model since removed
// would go on answering for a mesh that is not there.
export function dropModelSize(docId: string, index: number): void {
  const all = store.get(docId).byPart;
  if (!(String(index) in all)) return;
  const next = { ...all };
  delete next[String(index)];
  store.patch(docId, { byPart: next });
  writeState(docId, key(docId), next);
}

// AND WHAT SHAPE IT IS INSIDE THAT BOX — see partProfile, which argues what it holds and why
// "centred" needs it. Its own store and its own doc-state key, beside the size rather than
// inside it: a dozen readers want the three extents and exactly two want the profile, and a
// record that carried both would put a kilobyte of bands in front of every one of them.
//
// SAME LIFECYCLE, THOUGH, and that is the whole reason it lives in this file. Both are
// measurements of one mesh taken at one moment; a profile that outlived the size it was taken
// beside would answer about geometry the part no longer has.
export type ByPartProfile = Record<string, PartProfile>;

const profileKey = (docId: string) => `notice.parts.modelProfile.${docId}`;

const profiles = createDocEntryStore<{ byPart: ByPartProfile }>((docId) => ({
  snapshot: { byPart: readState<ByPartProfile>(docId, profileKey(docId), {}) },
}));

export const useModelProfiles = profiles.useSnapshot;

export const getModelProfiles = (docId: string): ByPartProfile => profiles.get(docId).byPart;

export const modelProfileOf = (
  docId: string,
  index: number | null | undefined,
): PartProfile | null => (index == null ? null : profiles.get(docId).byPart[String(index)] ?? null);

// WRITTEN WHEN THE MESH IS MEASURED, and only when the bands actually moved.
//
// The viewer re-measures on every load, and a write per mount is a doc-state save on a
// document several people have open — the same argument setModelSize makes, and the same
// guard, except that comparing a profile means comparing its bands. Stringified rather than
// walked: the values are already rounded on the way out of the sampler, so two readings of
// one unchanged mesh produce byte-identical JSON, and a walk would be a longer way to learn
// the same thing.
export function setModelProfile(docId: string, index: number, profile: PartProfile): void {
  const all = profiles.get(docId).byPart;
  const had = all[String(index)];
  if (had && JSON.stringify(had) === JSON.stringify(profile)) return;
  const next: ByPartProfile = { ...all, [String(index)]: profile };
  profiles.patch(docId, { byPart: next });
  writeState(docId, profileKey(docId), next);
}
